export enum CouponType {
  TRIAL = 'trial',
  EXTENSION = 'extension',
  AMOUNT = 'amount',
  PERCENT = 'percent',
  // SC: used for the redemption code of HUB device offer, applied with tmobile
  OFFER = 'offer',
  // SC: [stripe migration] For stripe 'promotion' discount. Unlike 'coupon' discounts (support issued),
  // 'promotion' discount (marketing offers) amount is taken off from base package price. Will not display discount line item.
  PRICE = 'price',
}

export type CouponItem = {
  code: string;
  benefitEffectiveUntil: string | null;
  extendedTrialWouldEndOn: string | null;
  benefitDurationMonths: number | null;
  benefitIsIndefinite?: boolean;
  benefitType: CouponType;
  benefitAmount: number | null;
  benefitSummary: string;
  longDescription: string;
  shortDescription: string;
  token: string;
  offerUrl?: string | null;
  terms_url: string;
  terms?: string;
  bearer?: boolean;
  redirectURL?: string | null;
  errorMessage?: string;
  partner?: any;
};

export enum OfferType {
  // SC NOTE: eventhough referral program has be removed, will honor the remaining referer credits user may have
  REFERRAL = 'referral',
  COUPON = 'coupon',
}

export type BillingLineItem = {
  key: string;
  title: string;
  amountText: string;
  amount?: number;
  durationText?: string;
  nextBillAt?: string;
};

export type BillingSummaryItem = {
  key: string;
  expandedItems: Array<BillingLineItem>;
  dueText: string;
  dueAmountText: string;
  isExpandable: boolean;
  isHighlighted?: boolean;
  identity: string;
  isInitiallyExpanded?: boolean;
  originalDueAmountText?: string;
  compact?: boolean;
  hidePlanLineItem?: boolean;
};

// SC NOTE: for web-login billingSummary experiment
export type BillingSummaryDetails = {
  expandedItems: BillingLineItem[];
  dueText: string;
  dueAmountText: string;
  dueDateText: string;
  originalDueAmountText?: string;
};

export enum Packages {
  PHILO_ARGON = 'philo-argon',
  PHILO_BORON = 'philo-boron',
  PHILO_CARBON = 'philo-carbon',
  AMC_PLUS = 'amcplus-adfree',
  BET_PLUS = 'betplus-adfree',
  ALLBLK_PLUS = 'allblk-adfree',
  PHILO_2021 = 'philo-2021',
  PHILO_2021_2m = 'philo-2021-2m',
  PHILO_2021_3m = 'philo-2021-3m',
  PHILO_DEACTIVATED = 'philo-deactivated',
  PHILO_PLUS = 'philo-plus',
  PHILO_FAST = 'philo-fast',
  EPIX = 'epix',
  STARZ = 'starz',
  MOVIE = 'movie',
  EMPTY_ID = '',
}

export enum PackageTypes {
  ADD_ON = 'addon',
  PLAN = 'plan',
  BUNDLE = 'bundle',
}

export type PackageId = Packages;

export interface JSONResponse {
  errors: Array<{ code: number; message: string }>;
  success: boolean;
}

export enum LogoColorKey {
  COLOR = 'large',
  DARK = 'large_dark',
  WHITE = 'large_white',
  SMALL = 'small',
  SMALL_DARK = 'small_dark',
  SMALL_WHITE = 'small_white',
}

export interface ChannelImages {
  small?: LogoColorKey.SMALL;
  // eslint-disable-next-line camelcase
  small_white?: LogoColorKey.SMALL_WHITE;
  // eslint-disable-next-line camelcase
  small_dark?: LogoColorKey.SMALL_DARK;
  // eslint-disable-next-line camelcase
  large_white?: LogoColorKey.WHITE;
  // eslint-disable-next-line camelcase
  large_dark?: LogoColorKey.DARK;
  large: LogoColorKey.COLOR;
}

export interface Channel {
  callsign: string;
  displayName: string;
  isFree: boolean;
  isFavorite: boolean;
  isFastViewable: boolean;
  images?: ChannelImages;
  name: string;
}

export interface Plan {
  id: PackageId;
  channels: Channel[];
  freeChannels: Channel[];
  name: string;
  price: number;
  basePrice: number;
}

export type BillItem = {
  id: PackageId;
  bundleId?: string;
  name: string;
  type: PackageTypes;
  unit: string;
  unitCount: number;
  price: number;
  priceCents: number;
  basePrice: number;
  basePriceCents: number; // SC: this is now the preferred field for billing summaries. We may move to deprecating the other price amount fields
  channels: Array<Channel>;
  freeChannels: Array<Channel>;
  keyImage: string;
  description: string;
  previouslySubscribed: boolean;
  proRationStart: string;
  proRationEnd: string;
  proRatedAmountCents: number;
  reportProRatedAmount: boolean;
  supportedAddons: PackageId[];
  nextBillAt: string;
  isTrialAvailable: boolean;
  trialDurationDays: number;
  trialExpiration: string;
  trialStarted: boolean;
  discountBenefit: OfferType;
  discountAmountCents: number;
  discountAfterNextBill: boolean;
  displayName?: string;
  termsUrl?: string;
};

export const NULL_PLAN = <BillItem>{};
export const EMPTY_BILL_ITEM = <BillItem>{};
export const EMPTY_BILLING_SUMMARY_ITEM = <BillingSummaryItem>{};

export type AddOnPromoDetails = {
  endDate: string;
  duration: number;
  unit: string;
};

export type PromotionalItem = {
  addons: BillItem[];
  coupons: string[];
  description: string;
  id: string;
  plan: BillItem;
  type: PackageTypes;
  termsUrl: string;
};

export enum ErrorCodes {
  PACKAGE_COUPON_NOT_FOR_PLAN = 1253,
  PACKAGE_ADDON_NOT_AVAILABLE = 1262,
  PACKAGE_OPTION_INVALID_FOR_BILLER = 1276,
  ERROR_SURGE_REQUEST_ENQUEUED = 1803,
  ERROR_SURGE_ENCOUNTER = 1806, // SC TODO: update name?
  INVALID_ZIP_CODE = 1102,
}

export enum PaymentTypes {
  CARD = 'card',
  WALLET = 'wallet',
  CASHAPP = 'cashapp',
  BANK = 'us_bank_account',
  LINK = 'link',
}

export enum PaymentBrands {
  APPLE = 'apple_pay',
  GOOGLE = 'google_pay',
}

export enum CardBrands {
  VISA = 'visa',
  MASTERCARD = 'mastercard',
  AMEX = 'amex',
  AMERICAN_EXPRESS = 'american_express',
  DISCOVER = 'discover',
}
